
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import hostlink from '../Hostlink/hostlink';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Home = () => {

    const [todaySalary, setTodaySalary] = useState([]);
    // =========================================================
    // USER
    // =========================================================

    const storedUser = localStorage.getItem('MYPCKARTUSER')
        ? JSON.parse(localStorage.getItem('MYPCKARTUSER'))[0]
        : null;

    const usershopid = storedUser?.id || '0';

    // =========================================================
    // DATES
    // Previous 7 days including today
    // =========================================================

    const today = new Date();

    const formatDateInput = (date) => {
        return date.toISOString().split('T')[0];
    };

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 6);

    const [fromdate, setFromdate] = useState(
        formatDateInput(sevenDaysAgo)
    );

    const [todate, setTodate] = useState(
        formatDateInput(today)
    );

    // =========================================================
    // DASHBOARD DATA
    // =========================================================

    const [purchaseData, setPurchaseData] = useState([]);
    const [purchaseGRData, setPurchaseGRData] = useState([]);
    const [saleData, setSaleData] = useState([]);
    const [saleReturnData, setSaleReturnData] = useState([]);

    const [fabricatorData, setFabricatorData] = useState([]);
    const [washData, setWashData] = useState([]);
    const [finishingData, setFinishingData] = useState([]);

    const [finalStockData, setFinalStockData] = useState([]);
    const [godownStockData, setGodownStockData] = useState([]);

    // =========================================================
    // EXISTING POS DATA
    // =========================================================

    const [totalRooms, setTotalRooms] = useState(0);
    const [runningRooms, setRunningRooms] = useState(0);
    const [availableRooms, setAvailableRooms] = useState(0);

    const [totalTables, setTotalTables] = useState(0);
    const [runningTables, setRunningTables] = useState(0);
    const [availableTables, setAvailableTables] = useState(0);

    const [rooms, setRooms] = useState([]);
    const [tables, setTables] = useState([]);
    const [orders, setOrders] = useState([]);

    // =========================================================
    // UI STATE
    // =========================================================

    const [loading, setLoading] = useState(false);

    // =========================================================
    // NUMBER FORMAT
    // =========================================================

    const formatNumber = (value, decimals = 2) => {
        return Number(value || 0).toLocaleString('en-IN', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    const compactNumber = (value) => {
        const number = Number(value || 0);

        if (Math.abs(number) >= 10000000) {
            return `${(number / 10000000).toFixed(2)} Cr`;
        }

        if (Math.abs(number) >= 100000) {
            return `${(number / 100000).toFixed(2)} L`;
        }

        if (Math.abs(number) >= 1000) {
            return `${(number / 1000).toFixed(1)} K`;
        }

        return formatNumber(number);
    };

    // =========================================================
    // FETCH HELPER
    // =========================================================

    const getData = async (url) => {
        try {
            const response = await axios.get(url);
            return Array.isArray(response.data)
                ? response.data
                : [];
        } catch (error) {
            console.error('API Error:', url, error);
            return [];
        }
    };

    // =========================================================
    // LOAD REPORTS
    // =========================================================

    const loadDashboard = async () => {

        try {

            setLoading(true);

            const [
                purchase,
                purchaseGR,
                sale,
                saleReturn,
                fabricator,
                wash,
                finishing,
                finalStock,
                godownStock,
                roomData,
                tableData,
                orderData
            ] = await Promise.all([

                // Purchase
                getData(
                    `${hostlink}/PurchaseMasviewrpt/0/${usershopid}/${fromdate}/${todate}`
                ),

                // Purchase GR
                getData(
                    `${hostlink}/PurchaseMasviewrpt/10/${usershopid}/${fromdate}/${todate}`
                ),

                // Sale
                getData(
                    `${hostlink}/PurchaseMasviewrpt/5/${usershopid}/${fromdate}/${todate}`
                ),

                // Sale Return
                getData(
                    `${hostlink}/PurchaseMasviewrpt/6/${usershopid}/${fromdate}/${todate}`
                ),

                // Fabricator
                getData(
                    `${hostlink}/fatchFabReport/${usershopid}/${fromdate}/${todate}/0/0`
                ),

                // Washing
                getData(
                    `${hostlink}/fatchWashReport/${usershopid}/${fromdate}/${todate}/0/0`
                ),

                // Finishing
                getData(
                    `${hostlink}/fatchFinishReport/${usershopid}/${fromdate}/${todate}/0/0`
                ),

                // Current Final Stock
                getData(
                    `${hostlink}/PurchaseMasFINALStock/${usershopid}`
                ),

                // Current Godown Stock
                getData(
                    `${hostlink}/PurchaseMasSTKGodwn/${usershopid}`
                ),

                // Existing rooms
                getData(
                    `${hostlink}/${usershopid}/roomstatus/0/0/3`
                ),

                // Existing tables
                getData(
                    `${hostlink}/${usershopid}/table`
                ),

                // Existing orders
                getData(
                    `${hostlink}/api/executeQuery?sqlQuery=select shopvno,blno,kottype from kotmas where shopid = ${usershopid} and kottype in (2,3) and kotdate = '${todate}' group by shopvno,blno,kottype`
                )
            ]);

            setPurchaseData(purchase);
            setPurchaseGRData(purchaseGR);
            setSaleData(sale);
            setSaleReturnData(saleReturn);

            setFabricatorData(fabricator);
            setWashData(wash);
            setFinishingData(finishing);

            setFinalStockData(finalStock);
            setGodownStockData(godownStock);

            // Rooms
            const totalRoomCount = roomData.length;

            const runningRoomCount =
                roomData.filter(
                    room => room?.stname === 'Booked'
                ).length;

            setTotalRooms(totalRoomCount);
            setRunningRooms(runningRoomCount);
            setAvailableRooms(
                totalRoomCount - runningRoomCount
            );
            setRooms(roomData);

            // Tables
            const totalTableCount = tableData.length;

            const runningTableCount =
                tableData.filter(
                    table => Number(table?.status) === 1
                ).length;

            setTotalTables(totalTableCount);
            setRunningTables(runningTableCount);
            setAvailableTables(
                totalTableCount - runningTableCount
            );
            setTables(tableData);

            // Orders
            setOrders(orderData);

            const today = new Date().toISOString().split("T")[0];

const todaySalaryRes = await axios.get(
    `${hostlink}/employee-salary/today`,
    {
        params: {
            shopid: usershopid,
            date: today
        }
    }
);


setTodaySalary(todaySalaryRes.data || []);

        } catch (error) {

            console.error(
                'Dashboard Load Error:',
                error
            );

            toast.error(
                'Unable to load dashboard data'
            );

        } finally {

            setLoading(false);

        }
    };

    // =========================================================
    // USER PASSWORD CHECK
    // =========================================================

    const checkUserPassword = async () => {

        const storedUser = localStorage.getItem(
            'MYPCKARTUSER'
        )
            ? JSON.parse(
                localStorage.getItem('MYPCKARTUSER')
            )[0]
            : null;

        const shopId = storedUser?.id || '0';
        const shopPassword =
            storedUser?.password || '';

        try {

            const response = await axios.get(
                `${hostlink}/shopmas/${shopId}`
            );

            if (
                response?.data?.password !==
                shopPassword
            ) {

                localStorage.removeItem(
                    'MYPCKARTUSER'
                );

                toast.error(
                    'Session expired'
                );
            }

        } catch (error) {

            console.error(
                'Password Check Error:',
                error
            );

        }
    };

    // =========================================================
    // INITIAL LOAD
    // =========================================================

    useEffect(() => {

        checkUserPassword();
        loadDashboard();

    }, []);

    // =========================================================
    // DATE PRESETS
    // =========================================================

    const setPrevious7Days = () => {

        const end = new Date();

        const start = new Date();
        start.setDate(end.getDate() - 6);

        setFromdate(formatDateInput(start));
        setTodate(formatDateInput(end));
    };

    const setYesterday = () => {

        const date = new Date();
        date.setDate(date.getDate() - 1);

        const formatted = formatDateInput(date);

        setFromdate(formatted);
        setTodate(formatted);
    };

    const setToday = () => {

        const formatted =
            formatDateInput(new Date());

        setFromdate(formatted);
        setTodate(formatted);
    };

    // =========================================================
    // KPIs
    // =========================================================

    const kpis = useMemo(() => {

        // Purchase
        const purchaseMtr =
            purchaseData.reduce(
                (total, res) =>
                    total +
                    Number(res?.mtr || 0),
                0
            );

        const purchaseRoll =
            purchaseData.reduce(
                (total, res) =>
                    total +
                    Number(res?.roll || 0),
                0
            );

        const purchaseAmount =
            purchaseData.reduce(
                (total, res) =>
                    total +
                    (
                        Number(res?.mtr || 0) *
                        Number(res?.rate || 0)
                    ),
                0
            );

        // Purchase GR
        const grMtr =
            purchaseGRData.reduce(
                (total, res) =>
                    total +
                    Number(res?.mtr || 0) * -1,
                0
            );

        // Sale
        const salePcs =
            saleData.reduce(
                (total, res) =>
                    total +
                    Number(res?.salestock || 0),
                0
            );

        // Return
        const returnPcs =
            saleReturnData.reduce(
                (total, res) =>
                    total +
                    Number(res?.salestock || 0) * -1,
                0
            );

        // Net Sale
        const netSale =
            salePcs - returnPcs;

        // Final Stock
        const finalStock =
            finalStockData.reduce(
                (total, res) =>
                    total +
                    Number(res?.stkqty || 0),
                0
            );

        // Godown
        const godownMtr =
            godownStockData.reduce(
                (total, res) =>
                    total +
                    Number(res?.stkqty || 0),
                0
            );

        const godownRoll =
            godownStockData.reduce(
                (total, res) =>
                    total +
                    Number(res?.stkqtyroll || 0),
                0
            );

        // Fabricator Pending
        const fabricatorPending =
            fabricatorData.reduce(
                (total, res) =>
                    total +
                    (
                        Number(res?.stkInfab || 0) +
                        Number(res?.inQty || 0) -
                        Number(res?.inQty2 || 0)
                    ),
                0
            );

        // Washing Pending
        const washPending =
            washData.reduce(
                (total, res) =>
                    total +
                    (
                        Number(res?.stkInfab || 0) +
                        Number(res?.inQty || 0) -
                        Number(res?.inQty2 || 0)
                    ),
                0
            );

        // Finishing Pending
        const finishingPending =
            finishingData.reduce(
                (total, res) =>
                    total +
                    (
                        Number(res?.stkInfab || 0) +
                        Number(res?.inQty || 0) -
                        Number(res?.inQty2 || 0)
                    ),
                0
            );

        return {
            purchaseMtr,
            purchaseRoll,
            purchaseAmount,
            grMtr,
            salePcs,
            returnPcs,
            netSale,
            finalStock,
            godownMtr,
            godownRoll,
            fabricatorPending,
            washPending,
            finishingPending
        };

    }, [
        purchaseData,
        purchaseGRData,
        saleData,
        saleReturnData,
        finalStockData,
        godownStockData,
        fabricatorData,
        washData,
        finishingData
    ]);

    // =========================================================
    // 7 DAY CHART DATA
    // =========================================================

    const chartData = useMemo(() => {

        const start = new Date(fromdate);
        const end = new Date(todate);

        const days = [];

        const cursor = new Date(start);

        while (cursor <= end) {

            const dateKey =
                formatDateInput(cursor);

            days.push({
                date: dateKey,
                label: cursor.toLocaleDateString(
                    'en-IN',
                    {
                        day: '2-digit',
                        month: 'short'
                    }
                ),
                purchase: 0,
                sale: 0,
                return: 0
            });

            cursor.setDate(
                cursor.getDate() + 1
            );
        }

        const getDateKey = (value) => {

            if (!value) {
                return '';
            }

            const date =
                new Date(value);

            if (Number.isNaN(date.getTime())) {
                return '';
            }

            return formatDateInput(date);
        };

        purchaseData.forEach((res) => {

            const key =
                getDateKey(res?.billdate);

            const item =
                days.find(
                    x => x.date === key
                );

            if (item) {

                item.purchase +=
                    Number(res?.mtr || 0);
            }
        });

        saleData.forEach((res) => {

            const key =
                getDateKey(res?.billdate);

            const item =
                days.find(
                    x => x.date === key
                );

            if (item) {

                item.sale +=
                    Number(res?.salestock || 0);
            }
        });

        saleReturnData.forEach((res) => {

            const key =
                getDateKey(res?.billdate);

            const item =
                days.find(
                    x => x.date === key
                );

            if (item) {

                item.return +=
                    Number(res?.salestock || 0) *
                    -1;
            }
        });

        return days;

    }, [
        fromdate,
        todate,
        purchaseData,
        saleData,
        saleReturnData
    ]);

    const maxChartValue = useMemo(() => {

        const values =
            chartData.flatMap(
                item => [
                    item.purchase,
                    item.sale,
                    item.return
                ]
            );

        return Math.max(
            ...values,
            1
        );

    }, [chartData]);

    // =========================================================
    // RECENT LOTS
    // =========================================================

    const recentSales = useMemo(() => {

        return saleData
            .slice(0, 8)
            .map((res, index) => ({
                id: index,
                lot:
                    res?.billno ||
                    '-',
                customer:
                    res?.custname ||
                    '-',
                style:
                    res?.style ||
                    '-',
                size:
                    res?.size ||
                    '-',
                pcs:
                    Number(
                        res?.salestock || 0
                    )
            }));

    }, [saleData]);

    // =========================================================
    // COMMON DATE HANDLER
    // =========================================================

    const handleFromDate = (e) => {
        setFromdate(e.target.value);
    };

    const handleToDate = (e) => {
        setTodate(e.target.value);
    };

    // =========================================================
    // RENDER
    // =========================================================

    return (

        <div
            style={{
                minHeight: '100vh',
                background: '#f4f7fb',
                color: '#1e293b',
                paddingBottom: '40px'
            }}
        >

            {/* =====================================================
                TOP HEADER
            ====================================================== */}

            <div
                style={{
                    background:
                        'linear-gradient(135deg,#0f172a,#1e3a8a)',
                    padding: '24px 24px 70px',
                    color: '#fff'
                }}
            >

                <div
                    className="container-fluid"
                >

                    <div
                        className="d-flex justify-content-between align-items-center flex-wrap gap-3"
                    >

                        <div
                            className="d-flex align-items-center"
                        >

                            <div
                                className="d-flex align-items-center justify-content-center me-3"
                                style={{
                                    width: '54px',
                                    height: '54px',
                                    borderRadius: '16px',
                                    background:
                                        'rgba(255,255,255,0.12)',
                                    border:
                                        '1px solid rgba(255,255,255,0.12)'
                                }}
                            >

                                <i
                                    className="fa-solid fa-chart-pie"
                                    style={{
                                        fontSize: '23px'
                                    }}
                                ></i>

                            </div>

                            <div>

                                <div
                                    style={{
                                        fontSize: '25px',
                                        fontWeight: '800'
                                    }}
                                >
                                    Management Dashboard
                                </div>

                                <div
                                    style={{
                                        fontSize: '13px',
                                        opacity: 0.78
                                    }}
                                >
                                    Purchase • Sales • Returns • Production • Stock
                                </div>

                            </div>

                        </div>


                        <div>

                            <button
                                type="button"
                                className="btn btn-light btn-sm"
                                onClick={loadDashboard}
                                disabled={loading}
                                style={{
                                    borderRadius: '10px',
                                    fontWeight: '700',
                                    padding:
                                        '9px 14px'
                                }}
                            >

                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1"></span>
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa-solid fa-rotate me-1"></i>
                                        Refresh
                                    </>
                                )}

                            </button>

                        </div>

                    </div>

                </div>

            </div>


            {/* =====================================================
                MAIN CONTENT
            ====================================================== */}

            <div
                className="container-fluid"
                style={{
                    marginTop: '-45px'
                }}
            >

                {/* =================================================
                    DATE FILTER
                ================================================== */}

                <div
                    className="card border-0 mb-3"
                    style={{
                        borderRadius: '18px',
                        boxShadow:
                            '0 10px 30px rgba(15,23,42,0.10)'
                    }}
                >

                    <div className="card-body p-3">

                        <div className="row g-2 align-items-end">

                            <div className="col-12 col-md-3">

                                <label
                                    className="form-label mb-1"
                                    style={{
                                        fontSize: '11px',
                                        fontWeight: '700'
                                    }}
                                >
                                    From Date
                                </label>

                                <input
                                    type="date"
                                    value={fromdate}
                                    onChange={
                                        handleFromDate
                                    }
                                    max={todate}
                                    className="form-control form-control-sm"
                                    style={{
                                        borderRadius: '10px'
                                    }}
                                />

                            </div>


                            <div className="col-12 col-md-3">

                                <label
                                    className="form-label mb-1"
                                    style={{
                                        fontSize: '11px',
                                        fontWeight: '700'
                                    }}
                                >
                                    To Date
                                </label>

                                <input
                                    type="date"
                                    value={todate}
                                    onChange={
                                        handleToDate
                                    }
                                    min={fromdate}
                                    className="form-control form-control-sm"
                                    style={{
                                        borderRadius: '10px'
                                    }}
                                />

                            </div>


                            <div className="col-12 col-md-6">

                                <div
                                    className="d-flex gap-2 flex-wrap"
                                >

                                    <button
                                        type="button"
                                        onClick={
                                            setPrevious7Days
                                        }
                                        className="btn btn-primary btn-sm"
                                        style={{
                                            borderRadius:
                                                '10px',
                                            fontWeight:
                                                '700'
                                        }}
                                    >
                                        <i className="fa-solid fa-calendar-week me-1"></i>
                                        Previous 7 Days
                                    </button>


                                    <button
                                        type="button"
                                        onClick={
                                            setYesterday
                                        }
                                        className="btn btn-outline-secondary btn-sm"
                                        style={{
                                            borderRadius:
                                                '10px'
                                        }}
                                    >
                                        Yesterday
                                    </button>


                                    <button
                                        type="button"
                                        onClick={
                                            setToday
                                        }
                                        className="btn btn-outline-secondary btn-sm"
                                        style={{
                                            borderRadius:
                                                '10px'
                                        }}
                                    >
                                        Today
                                    </button>


                                    <button
                                        type="button"
                                        onClick={
                                            loadDashboard
                                        }
                                        className="btn btn-success btn-sm px-3"
                                        style={{
                                            borderRadius:
                                                '10px',
                                            fontWeight:
                                                '700'
                                        }}
                                    >
                                        <i className="fa-solid fa-magnifying-glass me-1"></i>
                                        Apply
                                    </button>

                                </div>

                            </div>

                        </div>

                    </div>

                </div>


                {/* =================================================
                    TOP KPI CARDS
                ================================================== */}

                {todaySalary.length > 0 && (
    <div className="dashboard-section mt-4">

        <div className="section-header mb-3">
            <div>
                <h5 className="mb-1 fw-bold">
                    <i className="fa-solid fa-money-check-dollar me-2"></i>
                    Today's Salary
                </h5>
                <small className="text-muted">
                    Employees whose salary is due today
                </small>
            </div>
        </div>

        <div className="row g-3">

            {todaySalary.map((salary) => (
                <div
                    className="col-xl-4 col-lg-6 col-md-6"
                    key={salary.id}
                >
                    <div className="today-salary-card">

                        <div className="salary-card-top">

                            <div className="salary-icon">
                                <i className="fa-solid fa-user-tie"></i>
                            </div>

                            <div className="flex-grow-1 ms-3">
                                <div className="salary-employee">
                                    Employee #{salary.employeename}
                                </div>

                                <div className="salary-date">
                                    Salary Date:{" "}
                                    {salary.salaryDate || "-"}
                                </div>
                            </div>

                            <span
                                className={`salary-status ${
                                    salary.status === "PAID"
                                        ? "paid"
                                        : salary.status === "PARTIAL"
                                        ? "partial"
                                        : "open"
                                }`}
                            >
                                {salary.status || "OPEN"}
                            </span>

                        </div>

                        <div className="salary-amount-row">

                            <div>
                                <small>Net Salary</small>
                                <strong>
                                    ₹{Number(
                                        salary.netSalary || 0
                                    ).toLocaleString("en-IN")}
                                </strong>
                            </div>

                            <div>
                                <small>Paid</small>
                                <strong className="text-success">
                                    ₹{Number(
                                        salary.paidAmount || 0
                                    ).toLocaleString("en-IN")}
                                </strong>
                            </div>

                            <div>
                                <small>Balance</small>
                                <strong className="text-danger">
                                    ₹{Number(
                                        salary.balanceAmount || 0
                                    ).toLocaleString("en-IN")}
                                </strong>
                            </div>

                        </div>

                    </div>
                </div>
            ))}

        </div>
    </div>
)}
<br />

                <div className="row g-3 mb-3">

                    <DashboardCard
                        title="Purchase"
                        value={compactNumber(
                            kpis.purchaseMtr
                        )}
                        subtitle="Meter"
                        icon="fa-cart-shopping"
                        className="primary"
                    />

                    <DashboardCard
                        title="Purchase GR"
                        value={compactNumber(
                            kpis.grMtr
                        )}
                        subtitle="Meter received"
                        icon="fa-right-to-bracket"
                        className="info"
                    />

                    <DashboardCard
                        title="Sale"
                        value={compactNumber(
                            kpis.salePcs
                        )}
                        subtitle="Total pieces"
                        icon="fa-bag-shopping"
                        className="success"
                    />

                    <DashboardCard
                        title="Sale Return"
                        value={compactNumber(
                            kpis.returnPcs
                        )}
                        subtitle="Returned pieces"
                        icon="fa-rotate-left"
                        className="danger"
                    />

                </div>


                {/* =================================================
                    SECOND KPI CARDS
                ================================================== */}

                <div className="row g-3 mb-3">

                    <DashboardCard
                        title="Fabricator Pending"
                        value={compactNumber(
                            kpis.fabricatorPending
                        )}
                        subtitle="Production pending"
                        icon="fa-industry"
                        className="warning"
                    />

                    <DashboardCard
                        title="Washing Pending"
                        value={compactNumber(
                            kpis.washPending
                        )}
                        subtitle="Wash pending"
                        icon="fa-droplet"
                        className="purple"
                    />

                    <DashboardCard
                        title="Finishing Pending"
                        value={compactNumber(
                            kpis.finishingPending
                        )}
                        subtitle="Finishing pending"
                        icon="fa-check-double"
                        className="teal"
                    />

                    <DashboardCard
                        title="Final Stock"
                        value={compactNumber(
                            kpis.finalStock
                        )}
                        subtitle="Current stock"
                        icon="fa-layer-group"
                        className="dark"
                    />

                </div>


                {/* =================================================
                    CHART + OPERATIONS
                ================================================== */}

                <div className="row g-3 mb-3">

                    {/* ACTIVITY */}

                    <div className="col-12 col-xl-8">

                        <div
                            className="card border-0 h-100"
                            style={{
                                borderRadius:
                                    '18px',
                                boxShadow:
                                    '0 6px 22px rgba(15,23,42,0.06)'
                            }}
                        >

                            <div
                                className="card-body p-3"
                            >

                                <div
                                    className="d-flex justify-content-between align-items-center mb-3"
                                >

                                    <div>

                                        <div
                                            className="fw-bold"
                                            style={{
                                                fontSize:
                                                    '16px'
                                            }}
                                        >
                                            7 Day Activity
                                        </div>

                                        <div
                                            className="text-muted"
                                            style={{
                                                fontSize:
                                                    '11px'
                                            }}
                                        >
                                            Purchase vs Sale vs Return
                                        </div>

                                    </div>


                                    <span
                                        className="badge rounded-pill"
                                        style={{
                                            background:
                                                '#eef4ff',
                                            color:
                                                '#2563eb',
                                            padding:
                                                '8px 11px'
                                        }}
                                    >
                                        {chartData.length} Days
                                    </span>

                                </div>


                                {/* LEGEND */}

                                <div
                                    className="d-flex flex-wrap gap-3 mb-3"
                                >

                                    <Legend
                                        label="Purchase"
                                        dot="#2563eb"
                                    />

                                    <Legend
                                        label="Sale"
                                        dot="#16a34a"
                                    />

                                    <Legend
                                        label="Return"
                                        dot="#dc3545"
                                    />

                                </div>


                                {/* CHART */}

                                <div
                                    style={{
                                        display:
                                            'flex',
                                        alignItems:
                                            'flex-end',
                                        gap:
                                            '10px',
                                        height:
                                            '230px',
                                        padding:
                                            '10px 3px 0'
                                    }}
                                >

                                    {chartData.map(
                                        (item, index) => {

                                            const purchaseHeight =
                                                Math.max(
                                                    (
                                                        Math.abs(
                                                            item.purchase
                                                        ) /
                                                        maxChartValue
                                                    ) * 150,
                                                    item.purchase
                                                        ? 8
                                                        : 2
                                                );

                                            const saleHeight =
                                                Math.max(
                                                    (
                                                        Math.abs(
                                                            item.sale
                                                        ) /
                                                        maxChartValue
                                                    ) * 150,
                                                    item.sale
                                                        ? 8
                                                        : 2
                                                );

                                            const returnHeight =
                                                Math.max(
                                                    (
                                                        Math.abs(
                                                            item.return
                                                        ) /
                                                        maxChartValue
                                                    ) * 150,
                                                    item.return
                                                        ? 8
                                                        : 2
                                                );

                                            return (

                                                <div
                                                    key={index}
                                                    style={{
                                                        flex:
                                                            '1',
                                                        minWidth:
                                                            '50px',
                                                        height:
                                                            '100%',
                                                        display:
                                                            'flex',
                                                        flexDirection:
                                                            'column',
                                                        justifyContent:
                                                            'flex-end',
                                                        alignItems:
                                                            'center'
                                                    }}
                                                >

                                                    <div
                                                        className="d-flex align-items-end justify-content-center"
                                                        style={{
                                                            gap:
                                                                '3px',
                                                            height:
                                                                '180px',
                                                            width:
                                                                '100%'
                                                        }}
                                                    >

                                                        <div
                                                            title={`Purchase: ${formatNumber(item.purchase)}`}
                                                            style={{
                                                                width:
                                                                    '22%',
                                                                maxWidth:
                                                                    '24px',
                                                                height:
                                                                    `${purchaseHeight}px`,
                                                                background:
                                                                    '#2563eb',
                                                                borderRadius:
                                                                    '5px 5px 0 0'
                                                            }}
                                                        ></div>


                                                        <div
                                                            title={`Sale: ${formatNumber(item.sale)}`}
                                                            style={{
                                                                width:
                                                                    '22%',
                                                                maxWidth:
                                                                    '24px',
                                                                height:
                                                                    `${saleHeight}px`,
                                                                background:
                                                                    '#16a34a',
                                                                borderRadius:
                                                                    '5px 5px 0 0'
                                                            }}
                                                        ></div>


                                                        <div
                                                            title={`Return: ${formatNumber(item.return)}`}
                                                            style={{
                                                                width:
                                                                    '22%',
                                                                maxWidth:
                                                                    '24px',
                                                                height:
                                                                    `${returnHeight}px`,
                                                                background:
                                                                    '#dc3545',
                                                                borderRadius:
                                                                    '5px 5px 0 0'
                                                            }}
                                                        ></div>

                                                    </div>


                                                    <div
                                                        className="text-muted text-center mt-2"
                                                        style={{
                                                            fontSize:
                                                                '10px'
                                                        }}
                                                    >
                                                        {item.label}
                                                    </div>

                                                </div>

                                            );

                                        }
                                    )}

                                </div>

                            </div>

                        </div>

                    </div>


                    {/* OPERATIONS */}

                    <div className="col-12 col-xl-4">

                        <div
                            className="card border-0 h-100"
                            style={{
                                borderRadius:
                                    '18px',
                                boxShadow:
                                    '0 6px 22px rgba(15,23,42,0.06)'
                            }}
                        >

                            <div
                                className="card-body p-3"
                            >

                                <div className="fw-bold">
                                    Operations
                                </div>

                                <div
                                    className="text-muted mb-3"
                                    style={{
                                        fontSize:
                                            '11px'
                                    }}
                                >
                                    Current workload
                                </div>


                                <OperationProgress
                                    title="Fabricator"
                                    value={
                                        kpis.fabricatorPending
                                    }
                                    icon="fa-industry"
                                    max={
                                        Math.max(
                                            kpis.fabricatorPending,
                                            kpis.fabricatorPending *
                                                1.5,
                                            1
                                        )
                                    }
                                />


                                <OperationProgress
                                    title="Washing"
                                    value={
                                        kpis.washPending
                                    }
                                    icon="fa-droplet"
                                    max={
                                        Math.max(
                                            kpis.washPending,
                                            kpis.washPending *
                                                1.5,
                                            1
                                        )
                                    }
                                />


                                <OperationProgress
                                    title="Finishing"
                                    value={
                                        kpis.finishingPending
                                    }
                                    icon="fa-check-double"
                                    max={
                                        Math.max(
                                            kpis.finishingPending,
                                            kpis.finishingPending *
                                                1.5,
                                            1
                                        )
                                    }
                                />


                                <div
                                    className="mt-4 p-3"
                                    style={{
                                        borderRadius:
                                            '14px',
                                        background:
                                            '#f8fafc',
                                        border:
                                            '1px solid #edf2f7'
                                    }}
                                >

                                    <div
                                        className="d-flex justify-content-between"
                                    >

                                        <span
                                            className="text-muted"
                                            style={{
                                                fontSize:
                                                    '11px'
                                            }}
                                        >
                                            Godown Mtr
                                        </span>

                                        <strong
                                            style={{
                                                fontSize:
                                                    '14px'
                                            }}
                                        >
                                            {formatNumber(
                                                kpis.godownMtr
                                            )}
                                        </strong>

                                    </div>


                                    <div
                                        className="d-flex justify-content-between mt-2"
                                    >

                                        <span
                                            className="text-muted"
                                            style={{
                                                fontSize:
                                                    '11px'
                                            }}
                                        >
                                            Godown Roll
                                        </span>

                                        <strong
                                            style={{
                                                fontSize:
                                                    '14px'
                                            }}
                                        >
                                            {formatNumber(
                                                kpis.godownRoll
                                            )}
                                        </strong>

                                    </div>

                                </div>

                            </div>

                        </div>

                    </div>

                </div>


                

                <div className="row g-3 mb-3">

                    <ReportLinkCard
                        title="Purchase Report"
                        subtitle={`${formatNumber(kpis.purchaseMtr)} Mtr • ${formatNumber(kpis.purchaseRoll)} Roll`}
                        icon="fa-cart-shopping"
                        color="#2563eb"
                    />

                    <ReportLinkCard
                        title="Sale Report"
                        subtitle={`${formatNumber(kpis.salePcs)} Pcs`}
                        icon="fa-bag-shopping"
                        color="#16a34a"
                    />

                    <ReportLinkCard
                        title="Sale Return"
                        subtitle={`${formatNumber(kpis.returnPcs)} Pcs`}
                        icon="fa-rotate-left"
                        color="#dc3545"
                    />

                    <ReportLinkCard
                        title="Final Stock"
                        subtitle={`${formatNumber(kpis.finalStock)} Mtr`}
                        icon="fa-layer-group"
                        color="#7c3aed"
                    />

                </div>


                {/* =================================================
                    RECENT SALES
                ================================================== */}

                <div className="row g-3">

                    <div className="col-12 col-xl-8">

                        <div
                            className="card border-0"
                            style={{
                                borderRadius:
                                    '18px',
                                overflow:
                                    'hidden',
                                boxShadow:
                                    '0 6px 22px rgba(15,23,42,0.06)'
                            }}
                        >

                            <div
                                className="card-header bg-white border-0 d-flex justify-content-between align-items-center"
                                style={{
                                    padding:
                                        '15px 18px'
                                }}
                            >

                                <div>

                                    <div className="fw-bold">
                                        Recent Sale Lots
                                    </div>

                                    <small className="text-muted">
                                        Based on selected period
                                    </small>

                                </div>

                                <span
                                    className="badge rounded-pill"
                                    style={{
                                        background:
                                            '#eef4ff',
                                        color:
                                            '#2563eb',
                                        padding:
                                            '8px 12px'
                                    }}
                                >
                                    {recentSales.length}
                                </span>

                            </div>


                            <div
                                style={{
                                    overflowX:
                                        'auto'
                                }}
                            >

                                <table
                                    className="table table-hover align-middle mb-0"
                                    style={{
                                        minWidth:
                                            '750px',
                                        fontSize:
                                            '12px'
                                    }}
                                >

                                    <thead
                                        style={{
                                            background:
                                                '#f8fafc'
                                        }}
                                    >

                                        <tr>

                                            <th>
                                                Lot No
                                            </th>

                                            <th>
                                                Customer
                                            </th>

                                            <th>
                                                Style
                                            </th>

                                            <th>
                                                Size
                                            </th>

                                            <th className="text-end">
                                                Pcs
                                            </th>

                                        </tr>

                                    </thead>


                                    <tbody>

                                        {recentSales.length === 0 ? (

                                            <tr>

                                                <td
                                                    colSpan="5"
                                                    className="text-center text-muted"
                                                    style={{
                                                        padding:
                                                            '40px'
                                                    }}
                                                >
                                                    No sales found
                                                </td>

                                            </tr>

                                        ) : (

                                            recentSales.map(
                                                (item) => (

                                                    <tr
                                                        key={
                                                            item.id
                                                        }
                                                    >

                                                        <td
                                                            className="fw-semibold text-primary"
                                                        >
                                                            {
                                                                item.lot
                                                            }
                                                        </td>

                                                        <td>
                                                            {
                                                                item.customer
                                                            }
                                                        </td>

                                                        <td>
                                                            {
                                                                item.style
                                                            }
                                                        </td>

                                                        <td>
                                                            {
                                                                item.size
                                                            }
                                                        </td>

                                                        <td className="text-end fw-semibold">
                                                            {formatNumber(
                                                                item.pcs
                                                            )}
                                                        </td>

                                                    </tr>

                                                )
                                            )

                                        )}

                                    </tbody>

                                </table>

                            </div>

                        </div>

                    </div>


                    {/* STOCK SNAPSHOT */}

                    <div className="col-12 col-xl-4">

                        <div
                            className="card border-0 h-100"
                            style={{
                                borderRadius:
                                    '18px',
                                boxShadow:
                                    '0 6px 22px rgba(15,23,42,0.06)'
                            }}
                        >

                            <div className="card-body p-3">

                                <div className="fw-bold mb-1">
                                    Stock Snapshot
                                </div>

                                <div
                                    className="text-muted mb-3"
                                    style={{
                                        fontSize:
                                            '11px'
                                    }}
                                >
                                    Current inventory position
                                </div>


                                <StockRow
                                    label="Final Stock"
                                    value={
                                        kpis.finalStock
                                    }
                                    icon="fa-layer-group"
                                />


                                <StockRow
                                    label="Godown Mtr"
                                    value={
                                        kpis.godownMtr
                                    }
                                    icon="fa-warehouse"
                                />


                                <StockRow
                                    label="Godown Roll"
                                    value={
                                        kpis.godownRoll
                                    }
                                    icon="fa-boxes-stacked"
                                />


                                <StockRow
                                    label="Purchase Mtr"
                                    value={
                                        kpis.purchaseMtr
                                    }
                                    icon="fa-cart-shopping"
                                />


                                <StockRow
                                    label="Purchase GR"
                                    value={
                                        kpis.grMtr
                                    }
                                    icon="fa-right-to-bracket"
                                />

                            </div>

                        </div>

                    </div>

                </div>

            </div>


            {/* =====================================================
                FOOTER
            ====================================================== */}

            <div
                className="container-fluid mt-4"
            >

                <div
                    className="text-center text-muted"
                    style={{
                        fontSize:
                            '11px'
                    }}
                >
                    Dashboard period:
                    {' '}
                    <strong>
                        {fromdate}
                    </strong>
                    {' '}
                    to
                    {' '}
                    <strong>
                        {todate}
                    </strong>
                </div>

            </div>


            <ToastContainer
                position="top-right"
                autoClose={1500}
            />

        </div>
    );
};


// =============================================================
// DASHBOARD CARD
// =============================================================

const DashboardCard = ({
    title,
    value,
    subtitle,
    icon,
    className
}) => {

    const themes = {

        primary: {
            bg: '#eef4ff',
            icon: '#2563eb'
        },

        info: {
            bg: '#ecfeff',
            icon: '#0891b2'
        },

        success: {
            bg: '#ecfdf5',
            icon: '#16a34a'
        },

        danger: {
            bg: '#fff1f2',
            icon: '#dc3545'
        },

        warning: {
            bg: '#fff7ed',
            icon: '#ea580c'
        },

        purple: {
            bg: '#f5f3ff',
            icon: '#7c3aed'
        },

        teal: {
            bg: '#f0fdfa',
            icon: '#0f766e'
        },

        dark: {
            bg: '#f1f5f9',
            icon: '#334155'
        }
    };

    const theme =
        themes[className] ||
        themes.primary;

    return (

        <div
            className="col-12 col-sm-6 col-xl-3"
        >

            <div
                className="card border-0 h-100"
                style={{
                    borderRadius:
                        '18px',
                    boxShadow:
                        '0 6px 22px rgba(15,23,42,0.06)'
                }}
            >

                <div
                    className="card-body p-3"
                >

                    <div
                        className="d-flex justify-content-between align-items-start"
                    >

                        <div>

                            <div
                                className="text-muted"
                                style={{
                                    fontSize:
                                        '11px',
                                    fontWeight:
                                        '700',
                                    textTransform:
                                        'uppercase'
                                }}
                            >
                                {title}
                            </div>

                            <div
                                className="mt-1"
                                style={{
                                    fontSize:
                                        '25px',
                                    fontWeight:
                                        '800',
                                    color:
                                        '#0f172a'
                                }}
                            >
                                {value}
                            </div>

                            <div
                                className="text-muted mt-1"
                                style={{
                                    fontSize:
                                        '10px'
                                }}
                            >
                                {subtitle}
                            </div>

                        </div>


                        <div
                            className="d-flex align-items-center justify-content-center"
                            style={{
                                width:
                                    '46px',
                                height:
                                    '46px',
                                borderRadius:
                                    '14px',
                                background:
                                    theme.bg,
                                color:
                                    theme.icon
                            }}
                        >

                            <i
                                className={`fa-solid ${icon}`}
                            ></i>

                        </div>

                    </div>

                </div>

            </div>

        </div>
    );
};


// =============================================================
// LEGEND
// =============================================================

const Legend = ({
    label,
    dot
}) => {

    return (

        <div
            className="d-flex align-items-center"
        >

            <span
                style={{
                    width:
                        '9px',
                    height:
                        '9px',
                    borderRadius:
                        '50%',
                    background:
                        dot,
                    display:
                        'inline-block',
                    marginRight:
                        '6px'
                }}
            ></span>

            <span
                style={{
                    fontSize:
                        '11px',
                    color:
                        '#64748b'
                }}
            >
                {label}
            </span>

        </div>

    );
};


// =============================================================
// OPERATION PROGRESS
// =============================================================

const OperationProgress = ({
    title,
    value,
    icon,
    max
}) => {

    const percentage = Math.min(
        (Number(value || 0) /
            Number(max || 1)) *
            100,
        100
    );

    return (

        <div className="mb-3">

            <div
                className="d-flex justify-content-between mb-1"
            >

                <div
                    className="d-flex align-items-center gap-2"
                >

                    <i
                        className={`fa-solid ${icon} text-primary`}
                    ></i>

                    <span
                        style={{
                            fontSize:
                                '12px',
                            fontWeight:
                                '600'
                        }}
                    >
                        {title}
                    </span>

                </div>

                <strong
                    style={{
                        fontSize:
                            '12px'
                    }}
                >
                    {Number(value || 0).toLocaleString(
                        'en-IN',
                        {
                            maximumFractionDigits:
                                2
                        }
                    )}
                </strong>

            </div>


            <div
                style={{
                    height:
                        '7px',
                    background:
                        '#eef2f7',
                    borderRadius:
                        '999px',
                    overflow:
                        'hidden'
                }}
            >

                <div
                    style={{
                        width:
                            `${percentage}%`,
                        height:
                            '100%',
                        background:
                            'linear-gradient(90deg,#2563eb,#60a5fa)',
                        borderRadius:
                            '999px',
                        transition:
                            'width 0.4s ease'
                    }}
                ></div>

            </div>

        </div>

    );
};


// =============================================================
// INFO PANEL
// =============================================================

const InfoPanel = ({
    title,
    icon,
    children
}) => {

    return (

        <div
            className="card border-0 h-100"
            style={{
                borderRadius:
                    '18px',
                boxShadow:
                    '0 6px 22px rgba(15,23,42,0.06)'
            }}
        >

            <div className="card-body p-3">

                <div
                    className="d-flex align-items-center mb-3"
                >

                    <div
                        className="d-flex align-items-center justify-content-center me-2"
                        style={{
                            width:
                                '38px',
                            height:
                                '38px',
                            borderRadius:
                                '11px',
                            background:
                                '#eef4ff',
                            color:
                                '#2563eb'
                        }}
                    >
                        <i
                            className={`fa-solid ${icon}`}
                        ></i>
                    </div>

                    <div>

                        <div
                            className="fw-bold"
                            style={{
                                fontSize:
                                    '14px'
                            }}
                        >
                            {title}
                        </div>

                        <div
                            className="text-muted"
                            style={{
                                fontSize:
                                    '10px'
                            }}
                        >
                            Current status
                        </div>

                    </div>

                </div>

                {children}

            </div>

        </div>

    );
};


// =============================================================
// MINI METRIC
// =============================================================

const MiniMetric = ({
    label,
    value
}) => {

    return (

        <div
            className="col-4"
        >

            <div
                className="p-2 text-center"
                style={{
                    background:
                        '#f8fafc',
                    borderRadius:
                        '12px',
                    border:
                        '1px solid #eef2f7'
                }}
            >

                <div
                    style={{
                        fontSize:
                            '19px',
                        fontWeight:
                            '800'
                    }}
                >
                    {value}
                </div>

                <div
                    className="text-muted"
                    style={{
                        fontSize:
                            '9px'
                    }}
                >
                    {label}
                </div>

            </div>

        </div>

    );
};


// =============================================================
// REPORT LINK CARD
// =============================================================

const ReportLinkCard = ({
    title,
    subtitle,
    icon,
    color
}) => {

    return (

        <div className="col-12 col-md-6 col-xl-3">

            <div
                className="card border-0 h-100"
                style={{
                    borderRadius:
                        '16px',
                    boxShadow:
                        '0 5px 18px rgba(15,23,42,0.05)'
                }}
            >

                <div
                    className="card-body p-3"
                >

                    <div
                        className="d-flex align-items-center"
                    >

                        <div
                            className="d-flex align-items-center justify-content-center me-3"
                            style={{
                                width:
                                    '42px',
                                height:
                                    '42px',
                                borderRadius:
                                    '13px',
                                background:
                                    `${color}15`,
                                color:
                                    color
                            }}
                        >

                            <i
                                className={`fa-solid ${icon}`}
                            ></i>

                        </div>

                        <div>

                            <div
                                className="fw-bold"
                                style={{
                                    fontSize:
                                        '13px'
                                }}
                            >
                                {title}
                            </div>

                            <div
                                className="text-muted"
                                style={{
                                    fontSize:
                                        '10px'
                                }}
                            >
                                {subtitle}
                            </div>

                        </div>

                    </div>

                </div>

            </div>

        </div>
    );
};


// =============================================================
// STOCK ROW
// =============================================================

const StockRow = ({
    label,
    value,
    icon
}) => {

    return (

        <div
            className="d-flex align-items-center justify-content-between mb-3"
        >

            <div
                className="d-flex align-items-center"
            >

                <div
                    className="d-flex align-items-center justify-content-center me-2"
                    style={{
                        width:
                            '34px',
                        height:
                            '34px',
                        borderRadius:
                            '10px',
                        background:
                            '#f8fafc',
                        color:
                            '#2563eb'
                    }}
                >

                    <i
                        className={`fa-solid ${icon}`}
                    ></i>

                </div>

                <span
                    style={{
                        fontSize:
                            '11px',
                        fontWeight:
                            '600'
                    }}
                >
                    {label}
                </span>

            </div>

            <strong
                style={{
                    fontSize:
                        '13px'
                }}
            >
                {Number(value || 0).toLocaleString(
                    'en-IN',
                    {
                        minimumFractionDigits:
                            2,
                        maximumFractionDigits:
                            2
                    }
                )}
            </strong>

        </div>

    );
};

export default Home;

